import { DOCUMENT_ATTRIBUTE_KEYS, SEARCH_QUERY_VALIDITY_IN_HOURS, DEFAULT_SORT_ATTRIBUTE, IS_DEBUG } from './constants'
import { GetObjectCommand } from '@aws-sdk/client-s3'
import { getSignedUrl } from '@aws-sdk/s3-request-presigner'

/**
 * Checks if the given value is null or undefined.
 *
 * @param {any} it - The value to check.
 * @returns {boolean} True if the value is null or undefined, false otherwise.
 */
export const isNullOrUndefined = (it) => it === null || it === undefined

/**
 * Checks if the given value is null, undefined, or an empty string.
 *
 * @param {any} it - The value to check.
 * @returns {boolean} True if the value is null, undefined, or an empty string, false otherwise.
 */
export const isNullOrEmpty = (it) => isNullOrUndefined(it) || it.length === 0

/**
 * Unions and sorts a list of highlights, merging overlapping highlights.
 *
 * @param {Array<{ BeginOffset: number, EndOffset: number, TopAnswer: boolean }>} highlights - The list of highlights to union and sort.
 * @returns {Array<{ BeginOffset: number, EndOffset: number, TopAnswer: boolean }>} A new array of highlights where overlapping or adjacent highlights are merged.
 */
export function unionSortedHighlights(highlights) {
  if (isNullOrEmpty(highlights)) {
    return highlights
  }

  let prev = highlights[0]
  const unioned = [prev]
  for (let i = 1; i < highlights.length; i++) {
    const h = highlights[i]
    if (prev.EndOffset >= h.BeginOffset) {
      // union
      prev.EndOffset = Math.max(h.EndOffset, prev.EndOffset)
      prev.TopAnswer = prev.TopAnswer || h.TopAnswer
    } else {
      // disjoint, add to results
      unioned.push(h)
      prev = h
    }
  }

  return unioned
}

/**
 * Truncates a string to a maximum length, adding an ellipsis if the string is too long.
 *
 * @param {string} str - The input string to truncate.
 * @param {number} maxLen - The maximum length of the output string.
 * @returns {string} The truncated string, with an ellipsis added if the input string was too long.
 */
export function truncateString(str, maxLen) {
  if (maxLen < 5) {
    return `${str.substring(0, 3)}...`
  }

  const half = Math.ceil(maxLen / 2)

  return str.length < maxLen
    ? str
    : `${str.substring(0, half)}...${str.substring(str.length - (half - 5))}`
}

/**
 * Formats a date object as a localized string with a specific set of options.
 *
 * @param {Date} date - The date object to format.
 * @param {Object} [options={}] - The options object for formatting the date.
 * @param {string} [options.day='2-digit'] - The format for the day of the month.
 * @param {string} [options.hour='numeric'] - The format for the hour.
 * @param {string} [options.minute='numeric'] - The format for the minute.
 * @param {string} [options.month='short'] - The format for the month.
 * @param {string} [options.timeZone='UTC'] - The time zone to use for formatting.
 * @param {string} [options.timeZoneName='short'] - The format for the time zone name.
 * @param {string} [options.year='numeric'] - The format for the year.
 * @returns {string} The formatted date string, or an empty string if the input date is null or undefined.
 */
export const localizedDate = (
  date,
  options = {
    day: '2-digit',
    hour: 'numeric',
    minute: 'numeric',
    month: 'short',
    timeZone: 'UTC',
    timeZoneName: 'short',
    year: 'numeric'
  }
) => {
  return date ? date.toLocaleDateString('en', options) : ''
}

/**
 * Retrieves the updated timestamp from the provided document attributes.
 *
 * @param {Object} documentAttributes - The document attributes object.
 * @returns {Date|undefined} The updated timestamp, or undefined if not found.
 */
export const selectMostRecentUpdatedTimestamp = (documentAttributes) => {
  const updatedAt = documentAttributes[DOCUMENT_ATTRIBUTE_KEYS.UPDATED_AT]
  const createdAt = documentAttributes[DOCUMENT_ATTRIBUTE_KEYS.CREATED_AT]

  if (updatedAt && updatedAt.StringValue) {
    return new Date(updatedAt.StringValue)
  } else if (updatedAt && updatedAt.DateValue) {
    return updatedAt.DateValue
  } else if (createdAt && createdAt.StringValue) {
    return new Date(createdAt.StringValue)
  } else if (createdAt && createdAt.DateValue) {
    return createdAt.DateValue
  }
  return undefined
}

/**
 * Retrieves a presigned URL for an S3 object based on the provided document URI.
 *
 * @param {string} documentUri - The URI of the document, which may point to an S3 object.
 * @param {Object} s3Client - The S3 client instance to use for generating the presigned URL.
 * @returns {Promise<string>} The presigned URL for the S3 object, or the original document URI if it does not point to an S3 object.
 */
export const getS3PresignedUrl = async (documentUri, s3Client) => {
  const res = documentUri.split('/')
  if (res[2].startsWith('s3')) {
    // The URI points to an object in an S3 bucket
    // Get presigned url from s3
    const bucket = res[3]
    let key = res.slice(4).join('/')
    const params = { Bucket: bucket, Key: key }
    const command = new GetObjectCommand(params)
    return await getSignedUrl(s3Client, command)

  }
  return documentUri
}

/**
 * Constructs a query request object for the Kendra API.
 *
 * @see {@link https://docs.aws.amazon.com/AWSJavaScriptSDK/v3/latest/client/kendra/command/QueryCommand QueryCommand} for more information.
 *
 * @TODO Apply ...rest to the options object.
 *
 * @param {Object} options - Options for the query request.
 * @param {string} options.indexId - The identifier of the index to search.
 * @param {string} options.queryText - The search query text.
 * @param {number} options.pageNumber - Query results are returned in pages the size of the PageSize parameter. By default, Amazon Kendra returns the first page of results. Use this parameter to get result pages after the first one. (default: 1).
 * @param {number} [options.pageSize] - Sets the number of results that are returned in each page of results. The default page size is 10. The maximum number of results returned is 100. If you ask for more than 100 results, only 100 are returned.
 * @param {Object} [options.filter] - The attribute filter for the search query (e.g., { AND: [{ Key: 'CATEGORY', Value: 'FAQ' }] }).
 * @param {string} [options.sort] - The name of the document attribute used to sort the response. (e.g., '_created_at').
 * @param {string} [options.order] - The order that the results should be returned in. In case of ties, the relevance assigned to the result by Amazon Kendra is used as the tie-breaker. (e.g., 'ASC', 'DESC').
 * @param {string} [options.type] - Sets the type of query result or response. Only results for the specified type are returned. (e.g., 'ANSWER', 'QUESTION_ANSWER', 'DOCUMENT').
 * @param {Object} rest - Additional options for the query request.
 * @returns {Object} The partially-constructed query request object for the Kendra API.
 */
export const constructQueryRequest = ({
  indexId,
  queryText,
  pageNumber,
  filter,
  sort,
  order,
  type,
  pageSize,
  ...rest
}) => {
  const queryRequest = {
    IndexId: indexId,
    QueryText: queryText,
    PageNumber: pageNumber
  }
  if (filter) {
    queryRequest.AttributeFilter = filter
  }
  if (sort && order && sort !== DEFAULT_SORT_ATTRIBUTE) {
    queryRequest.SortingConfiguration = {
      DocumentAttributeKey: sort,
      SortOrder: order
    }
  }
  if (type) {
    queryRequest.QueryResultTypeFilter = type
  }
  if (pageSize) {
    queryRequest.PageSize = pageSize
  }
  return queryRequest
}

/**
 * Processes the result items by checking for S3 document links and converting them if needed.
 * @see {@link https://docs.aws.amazon.com/AWSJavaScriptSDK/v3/latest/Package/-aws-sdk-client-kendra/Interface/QueryResultItem QueryResultItem} for more information.
 *
 * @param {Object} results - The results object containing the array of QueryResultItem objects.
 * @param {S3} s3 - The S3 client instance.
 */
export const processResultItems = async (results, s3) => {
  // For each result in the array:
  // First, check if it has an S3 document link and convert it if needed
  // Then check its type and add it to the correct temporary array
  // If the type doesn't match any category, skip it (default case)
  for (const result of results.ResultItems) {
    if (!isNullOrUndefined(s3) && result.DocumentURI) {
      try {
        result.DocumentURI = await getS3PresignedUrl(result.DocumentURI, s3)
      } catch (e) {
        console.error('Error fetching pre-signed URL:', e)
      } finally {
        console.log('Pre-signed URL fetched successfully')
      }
    }
  }
}

/**
 * Checks if a search query is valid based on the time difference between the current time and the provided date.
 *
 * @param {Date} date - The date associated with the search query.
 * @returns {boolean} - True if the search query is valid, false otherwise.
 */
const isValidQuery = (date) => {
  const currentDate = new Date().getTime()
  const timeDiffInHours = currentDate - new Date(date).getTime()
  const hoursDifference = timeDiffInHours / (1000 * 60 * 60)

  return hoursDifference < SEARCH_QUERY_VALIDITY_IN_HOURS
}

/**
 * Removes expired search logs from the provided array based on the validity of the search query.
 *
 * @param {Object[]} searchLogs - An array of search log objects.
 * @returns {Object[]} - A new array containing only the search logs with valid queries.
 */
export const removeExpiredQuery = (searchLogs) => {
  return searchLogs.filter(log => isValidQuery(log.created_at));
}

/**
 * Generates a lookup object that maps data source IDs to their corresponding names.
 *
 * @param {object[]} dataSources - An array of data source objects, each containing an Id and Name property.
 * @returns {object} - A lookup object where the keys are the data source IDs and the values are the data source names.
 */
export function getDataSourceNameLookup( dataSources ) {
  if (!dataSources) {
    return null
  }

  const names = {}
  for (const ds of dataSources) {
    if (ds && ds.Id && ds.Name) {
      names[ds.Id] = ds.Name
    }
  }

  return names
}

/**
 * Logs a message to the console if the provided `isDebug` flag is true.
 *
 * @param {boolean} isDebug - A flag indicating whether to log the message or not.
 * @param {string} title - The title of the log message.
 * @param {string} description - The description of the log message.
 */
const logger = (isDebug, title, description) => {
  if (isDebug) {
    console.groupCollapsed(title)
    console.log(description)
    console.groupEnd()
  }
}

/**
 * Logs a message to the console if the provided `IS_DEBUG` flag is true.
 * The flag is mapped to `env.stage === 'dev'` in constants.js
 *
 * @param {string} title - The title of the log message.
 * @param {string} description - The description of the log message.
 * @returns {void}
 */
export const curryLogger = (title, description) => logger(IS_DEBUG, title, description)

